import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import Preloader from '../../components/Preloader';
import api from '../../services/api';

const DoctorProtocols = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      setLoading(true);
      loadAssignments().then(() => setLoading(false));
    }
  }, [user]);

  useEffect(() => {
    if (assignments.length > 0) {
      setLoading(true);
      loadProtocols().then(() => setLoading(false));
    }
  }, [assignments]);

  const loadAssignments = async () => {
    try {
      const response = await api.get(`/assignments?doctor_id=${user.id}&status=active,assigned_to_doctor`);
      setAssignments(response.data.assignments || []);
    } catch (err) {
      setAssignments([]);
    }
  };

  const loadProtocols = async () => {
    try {
      const response = await api.get('/protocols?include_questions=true');
      const allProtocols = response.data.protocols || [];
      const assignedProtocolIds = [...new Set(assignments.map(a => a.protocol_id))];
      const doctorProtocols = allProtocols.filter(p => assignedProtocolIds.includes(p.id));
      const enhancedProtocols = doctorProtocols.map(protocol => {
        const protocolAssignments = assignments.filter(a => a.protocol_id === protocol.id);
        const caseCount = protocolAssignments.length;
        return {
          ...protocol,
          assignments: protocolAssignments,
          case_count: caseCount,
          totalCases: caseCount
        };
      });
      setProtocols(enhancedProtocols);
    } catch (err) {
      setProtocols([]);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await api.get('/questions');
      setQuestions(response.data.questions || []);
    } catch {
      setQuestions([]);
    }
  };

  const getQuestionTypeIcon = (type) => {
    return type === 'mcq' ? 'fas fa-list-ul' : 'fas fa-toggle-on';
  };

  const getQuestionTypeLabel = (type) => {
    return type === 'mcq' ? 'Multiple Choice' : 'True/False';
  };

  if (loading) {
    return (
      <ResearchLayout pageTitle="My Protocols">
        <Preloader message="Loading your assigned protocols..." />
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="My Protocols">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-secondary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-clipboard-list me-2"></i>
                  My Assigned Protocols
                </h5>
              </div>
              <div className="card-body">
                {protocols.length > 0 ? (
                  <div className="row">
                    {protocols.map((protocol) => (
                      <div key={protocol.id} className="col-lg-6 col-md-12 mb-4">
                        <div className="card border border-secondary h-100">
                          <div className="card-header bg-light d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 text-secondary fw-bold">
                              {protocol.name}
                            </h6>
                            <span className="badge bg-secondary">
                              {protocol.case_count || 0} Cases
                            </span>
                          </div>
                          <div className="card-body">
                            <p className="text-muted mb-3">
                              {protocol.description}
                            </p>
                            
                            <div className="mb-3">
                              <strong className="text-dark">
                                Questions ({Array.isArray(protocol.questions) ? protocol.questions.length : 0}):
                              </strong>
                              <div className="mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {Array.isArray(protocol.questions) && protocol.questions.length > 0 ? (
                                  protocol.questions.map((question, idx) => (
                                    <div key={question.id} className="card mb-2 border-0 bg-light">
                                      <div className="card-body p-2">
                                        <div className="d-flex justify-content-between align-items-start">
                                          <div className="flex-grow-1">
                                            <div className="small fw-bold text-secondary">
                                              Question {idx + 1}
                                            </div>
                                            <div className="small text-dark">
                                              {question.text}
                                            </div>
                                          </div>
                                          <div className="ms-2">
                                            <span className="badge bg-secondary small">
                                              <i className={`${question.type === 'mcq' ? 'fas fa-list-ul' : 'fas fa-toggle-on'} me-1`}></i>
                                              {question.type === 'mcq' ? 'Multiple Choice' : 'True/False'}
                                            </span>
                                          </div>
                                        </div>
                                        {question.choices && (
                                          <div className="mt-2">
                                            <div className="small text-muted">
                                              Options: {question.choices.join(', ')}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-muted small">No questions available.</div>
                                )}
                              </div>
                            </div>
                            
                            <div className="d-flex justify-content-between align-items-center">
                              <small className="text-muted">
                                <i className="fas fa-calendar me-1"></i>
                                Assigned: {new Date(protocol.assignments[0]?.assignedAt).toLocaleDateString()}
                              </small>
                              <div>
                                <button
                                  className="btn btn-outline-secondary btn-sm me-2"
                                  onClick={() => setSelectedProtocol(
                                    selectedProtocol === protocol.id ? null : protocol.id
                                  )}
                                >
                                  <i className="fas fa-eye me-1"></i>
                                  {selectedProtocol === protocol.id ? 'Hide Details' : 'View Details'}
                                </button>
                                <span className="badge bg-success">
                                  <i className="fas fa-check me-1"></i>
                                  Active
                                </span>
                              </div>
                            </div>

                            {/* Expanded Details */}
                            {selectedProtocol === protocol.id && (
                              <div className="mt-3 pt-3 border-top">
                                <h6 className="text-secondary mb-3">Protocol Details</h6>
                                <div className="row">
                                  <div className="col-md-6">
                                    <p><strong>Total Questions:</strong> {Array.isArray(protocol.questions) ? protocol.questions.length : 0}</p>
                                    <p><strong>Multiple Choice:</strong> {Array.isArray(protocol.questions) ? protocol.questions.filter(q => q.type === 'mcq').length : 0}</p>
                                    <p><strong>True/False:</strong> {Array.isArray(protocol.questions) ? protocol.questions.filter(q => q.type === 'boolean').length : 0}</p>
                                  </div>
                                  <div className="col-md-6">
                                    <p><strong>Assigned Cases:</strong> {protocol.totalCases}</p>
                                    <p><strong>Status:</strong> <span className="text-success">Active</span></p>
                                    <p><strong>Created:</strong> {new Date(protocol.createdAt).toLocaleDateString()}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i className="fas fa-clipboard-list fa-4x text-muted mb-4"></i>
                    <h4 className="text-muted">No Assigned Protocols</h4>
                    <p className="text-muted">
                      You haven't been assigned any protocols yet. Check back later or contact your Medical Admin.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default DoctorProtocols;